import cx from 'clsx';
import * as React from 'react';

import { useWindowSize } from '../../../hooks/useWindowSize';
import { CallbackMode } from '../../../logic/main/types';
import { QuicksilverTileOutfitType } from '../../game-tile';
import JackpotsWrapper, {
  JackpotsWrapperPropsInterface,
} from '../../jackpots-wrapper/JackpotsWrapper';
import ClosedOverlay from '../ClosedOverlay';
import { MEDIUM_BREAKPOINT, MEDIUM_OUTFIT } from '../constants';
import GhostTile from '../GhostTile';
import ModularGridGameTile from '../ModularGridGameTile';
import {
  GameClickProps,
  GameTileProps,
  GridSizeType,
  OverlayProps,
} from '../types';
import { prepareGameList, selectProperStyles } from '../utils';
import styles from './JackpotGrid.module.scss';

interface JackpotGridProps {
  jackpotSlot?: JackpotsWrapperPropsInterface;
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game?: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game?: GameClickProps;
    path: string;
  }) => void;
  overlay?: OverlayProps;
  gameDetailsInfo: boolean;
  callbackMode: CallbackMode;
  type: 'Hero' | 'Banner';
  size?: GridSizeType;
  gamesLimit?: number;
  initialIndex?: number;
}

const JackpotGrid: React.FC<JackpotGridProps> = ({
  jackpotSlot,
  onClick,
  gameList,
  onInfoClick,
  overlay,
  gameDetailsInfo,
  callbackMode,
  type,
  size,
  gamesLimit = 3,
  initialIndex = 0,
}) => {
  const windowWidth = useWindowSize().width;
  if (!gameList && !overlay) return null;
  const preparedGameList = prepareGameList(gameList, gamesLimit);
  const createSlot = (
    gamePosition: number,
    outfit: QuicksilverTileOutfitType,
    slot: GameTileProps,
    tileSize: 'large' | 'small'
  ) => (
    <div
      className={styles.gridSlot}
      data-testid={`${gamePosition}-slot`}
      key={`${gamePosition}-key`}
    >
      <ModularGridGameTile
        data-testid="modular-grid-tile"
        game={slot}
        onClick={handleClick(gamePosition, onClick)}
        onInfoClick={handleClick(gamePosition, onInfoClick)}
        outfit={outfit}
        isAnchor
        ghost={!!overlay}
        gameDetailsInfo={gameDetailsInfo}
        callbackMode={callbackMode}
        tileSize={tileSize}
      />
    </div>
  );

  const handleClick = (gamePosition: number, fn: Function) => (
    game: GameClickProps,
    path?: string
  ) => {
    fn({ game, gamePosition, ...(path && { path }) });
  };

  return (
    <div className={cx(styles.container)} data-testid="jackpot-grid-grid">
      <div className={cx(styles.rowsWrapper)}>
        <div
          className={cx(
            type === 'Banner' &&
              selectProperStyles(
                {
                  small: styles.jackpotBannerSmallForced,
                  medium: styles.jackpotBannerMediumForced,
                  default: [
                    styles.jackpotBannerSmall,
                    styles.jackpotBannerMedium,
                  ],
                },
                size
              ),
            {
              [styles.jackpotBannerSlot]: type === 'Banner',
              [styles.jackpotHeroSlot]: type === 'Hero',
            }
          )}
          data-testid="jackpot-slot"
        >
          {!overlay ? (
            jackpotSlot && (
              <JackpotsWrapper
                {...jackpotSlot}
                size={type === 'Banner' ? 'wide' : 'slim'}
                scaleLogo={
                  (type === 'Banner' &&
                    size === undefined &&
                    windowWidth < MEDIUM_BREAKPOINT.min) ||
                  (type === 'Banner' && size === 'small')
                }
              />
            )
          ) : (
            <GhostTile />
          )}
        </div>
        {(type === 'Hero' ||
          (windowWidth >= MEDIUM_BREAKPOINT.min && size === undefined) ||
          size === 'medium') && (
          <div className={styles.heroRow}>
            {preparedGameList
              .slice(0, 2)
              .map((g, i) =>
                createSlot(i + 1 + initialIndex, MEDIUM_OUTFIT, g, 'small')
              )}
          </div>
        )}
        {((windowWidth >= MEDIUM_BREAKPOINT.min && size === undefined) ||
          size === 'medium') && (
          <div className={styles.rowsWrapper}>
            <div className={styles.mediumRow}>
              {createSlot(2, MEDIUM_OUTFIT, preparedGameList[2], 'large')}
            </div>
            <div className={styles.mediumRow}>
              {createSlot(3, MEDIUM_OUTFIT, preparedGameList[3], 'small')}
              {createSlot(4, MEDIUM_OUTFIT, preparedGameList[4], 'small')}
            </div>
          </div>
        )}
      </div>
      <div
        className={cx(
          selectProperStyles(
            {
              small: styles.rowSmallForced,
              medium: styles.rowMediumForced,
              default: [styles.rowSmall, styles.rowMedium],
            },
            size
          )
        )}
      >
        {preparedGameList
          .slice(type === 'Hero' ? 2 : 0, type === 'Hero' ? 5 : 3)
          .map((g, i) => {
            return createSlot(
              i + 2 + initialIndex + (type === 'Hero' ? 2 : 0),
              MEDIUM_OUTFIT,
              g,
              'small'
            );
          })}
      </div>
      {!!overlay && <ClosedOverlay {...overlay} />}
    </div>
  );
};

export default JackpotGrid;
